/**
 * ============================================================================
 * useImpactSetQuery — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the active Impact Set of a Change Control.
 *          The server returns the impact items grouped by category along with
 *          the confidence breakdown used for the side-panel summary.
 * Endpoint: GET /api/v1/changes/{id}/impact-set
 *
 * NOTE on confidence:
 *   - HIGH    = direct rule match (e.g., training task is bound to the doc).
 *   - MEDIUM  = transitive inference (e.g., child equipment qualifications).
 *   - LOW     = fuzzy text match flagged for human review.
 *   Per CCIE-CONF-001, MEDIUM and LOW are collapsed by default.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type ImpactCategory =
  | 'DOCUMENT'
  | 'TRAINING_TASK'
  | 'EQUIPMENT'
  | 'SUPPLIER'
  | 'PRODUCT'
  | 'REGULATORY_ANCHOR'
  | 'VALIDATION';

export type Confidence = 'HIGH' | 'MEDIUM' | 'LOW';

export type ChangeStatus =
  | 'DRAFT'
  | 'PROPOSED'
  | 'ACKNOWLEDGED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CLOSED';

export interface ImpactItem {
  item_id: string;
  category: ImpactCategory;
  entity_id: string;
  entity_kind: string;
  entity_display_name: string;
  confidence: Confidence;
  discovery_rule: string;
  rationale: string;
  /** Arbitrary per-item extras — never use `any`, narrow at call sites. */
  payload: Record<string, unknown>;
}

export interface ImpactSetSummary {
  total_items: number;
  by_category: Partial<Record<ImpactCategory, number>>;
  by_confidence: Record<Confidence, number>;
}

export interface ImpactSetResponse {
  change_id: string;
  change_type: string;
  status: ChangeStatus;
  subject_entity_id: string;
  subject_entity_kind: string;
  subject_entity_display_name: string;
  title: string;
  rationale: string;
  impact_set_id: string;
  computed_at_utc: string;
  /** Sub-second target, < 3 s SLO. */
  compute_duration_ms: number;
  items: ImpactItem[];
  summary: ImpactSetSummary;
}

export function useImpactSetQuery(changeId: string): UseQueryResult<ImpactSetResponse> {
  return useQuery<ImpactSetResponse>({
    queryKey: ['changes', changeId, 'impact-set'],
    enabled: !!changeId,
    queryFn: ({ signal }) =>
      apiFetch<ImpactSetResponse>(
        `/api/v1/changes/${encodeURIComponent(changeId)}/impact-set`,
        { signal },
      ),
  });
}
